import { useState, useEffect, useCallback } from 'react';
import { AccountingSubNav } from '../components/AccountingSubNav';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { StatCard, StatGrid } from '@/shared/components/ui/StatCard';
import { Button } from '@/shared/components/ui/Button';
import { api } from '@/shared/api/axios';
import { useUserOptions } from '@/shared/hooks/useUserOptions';
import { useUnits, useProducts } from '@/features/catalog/hooks/useCatalogApi';
import { useMasterItems } from '@/features/master-data/hooks/useMasters';
import { DEPARTMENT_OPTIONS, PR_PRIORITY_OPTIONS, PR_PRIORITY_TONE, majorToMinor } from '@/features/procurement-rfq/constants';
import {
  ShoppingCart, Package, FileText, ReceiptText, RotateCcw,
  Plus, Loader2, X, AlertTriangle,
} from 'lucide-react';
import { localToday } from '@/shared/utils/utils';

// ── Utility ───────────────────────────────────────────────────────────────────
function fmt(minor: number) { return '₹' + (minor / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 }); }
function fmtDate(d: string | null) { return d ? new Date(d).toLocaleDateString('en-IN') : '—'; }

// ── Status badges ─────────────────────────────────────────────────────────────
const STATUS_COLORS: Record<string, string> = {
  draft: 'bg-slate-100 text-slate-600',
  submitted: 'bg-blue-100 text-blue-700',
  approved: 'bg-emerald-100 text-emerald-700',
  sent: 'bg-sky-100 text-sky-700',
  partially_received: 'bg-amber-100 text-amber-700',
  received: 'bg-green-100 text-green-700',
  partially_billed: 'bg-orange-100 text-orange-700',
  closed: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-red-100 text-red-700',
  posted: 'bg-emerald-100 text-emerald-700',
  partially_paid: 'bg-amber-100 text-amber-700',
  paid: 'bg-green-100 text-green-700',
  po_raised: 'bg-indigo-100 text-indigo-700',
  rejected: 'bg-red-100 text-red-700',
};

function StatusBadge({ status }: { status: string }) {
  return (
    <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_COLORS[status] || 'bg-slate-100 text-slate-500'}`}>
      {status.replace(/_/g, ' ')}
    </span>
  );
}

// ── Purchase Requisition line shape ───────────────────────────────────────────
// Mirrors the `lines[]` subdocument on PurchaseRequisitionModel
// (src/db-procurement.js). `expectedRate` / `targetRate` are held in MAJOR
// units (rupees) here because that is what the user types; they are converted
// to the schema's `expectedRateMinor` / `targetRateMinor` on submit via the
// shared majorToMinor helper. Nothing else in this file is in major units.
type PRLine = {
  description: string;
  quantity: number;
  estimatedUnitCostMinor: number;
  productId: string;
  materialCategory: string;
  specification: string;
  quantityUnitId: string;
  quantityUnit: string;
  expectedRate: string;
  targetRate: string;
};

const EMPTY_PR_LINE: PRLine = {
  description: '', quantity: 1, estimatedUnitCostMinor: 0,
  productId: '', materialCategory: '', specification: '',
  quantityUnitId: '', quantityUnit: '', expectedRate: '', targetRate: '',
};

/** '' is not castable to an ObjectId — omit the key rather than send a blank. */
function optionalId(value: string): string | undefined {
  const v = String(value || '').trim();
  return v ? v : undefined;
}

/** unitId comes back either populated or as a bare id depending on the route. */
function idOf(ref: any): string {
  if (!ref) return '';
  return typeof ref === 'string' ? ref : String(ref._id || '');
}

// ── Read-only stock position for a selected product ───────────────────────────
// Current stock, minimum stock and stock-in-transit all come from
// GET /api/accounting/p2p/product-stock/:productId. Nothing is computed here:
// `minimumStock` is derived server-side from CatalogProduct's four overlapping
// thresholds and `inTransit` from src/shared/procurement/stock-in-transit.js,
// so the requester sees exactly the figures the server would act on.
type ProductStock = {
  name: string; sku: string;
  currentStock: number; minimumStock: number;
  inTransit: number; openOrderCount: number;
  unitId: string | null;
};

function ProductStockPanel({ productId }: { productId: string }) {
  const [stock, setStock] = useState<ProductStock | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!productId) { setStock(null); setError(''); return; }
    let cancelled = false;
    setLoading(true); setError('');
    api.get(`/api/accounting/p2p/product-stock/${productId}`)
      .then(res => { if (!cancelled) setStock(res.data?.data || null); })
      .catch((err: any) => { if (!cancelled) setError(err.response?.data?.error?.message || err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [productId]);

  if (!productId) return null;

  const cellCls = 'rounded-lg bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 px-3 py-2';
  if (loading) return <div className="flex items-center gap-2 text-xs text-slate-400 py-2"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading stock position…</div>;
  if (error) return <div className="text-xs text-red-500 py-2">Stock position unavailable: {error}</div>;
  if (!stock) return null;

  const below = stock.minimumStock > 0 && stock.currentStock < stock.minimumStock;

  return (
    <div className="mt-2">
      <div className="grid grid-cols-3 gap-2">
        <div className={cellCls}>
          <div className="text-[10px] uppercase tracking-wide text-slate-400">Current Stock</div>
          <div className={`text-sm font-bold ${below ? 'text-red-600' : 'text-slate-700 dark:text-slate-200'}`}>{stock.currentStock}</div>
        </div>
        <div className={cellCls}>
          <div className="text-[10px] uppercase tracking-wide text-slate-400">Minimum Stock</div>
          <div className="text-sm font-bold text-slate-700 dark:text-slate-200">{stock.minimumStock}</div>
        </div>
        <div className={cellCls}>
          <div className="text-[10px] uppercase tracking-wide text-slate-400">In Transit</div>
          <div className="text-sm font-bold text-sky-700 dark:text-sky-400">
            {stock.inTransit}
            {stock.openOrderCount > 0 && <span className="ml-1 text-[10px] font-normal text-slate-400">({stock.openOrderCount} open PO{stock.openOrderCount === 1 ? '' : 's'})</span>}
          </div>
        </div>
      </div>
      {below && (
        <div className="flex items-center gap-1.5 mt-1.5 text-[11px] text-amber-700 dark:text-amber-400">
          <AlertTriangle className="w-3 h-3" /> On hand is below the minimum stock level.
        </div>
      )}
    </div>
  );
}

// ── New PR Modal ──────────────────────────────────────────────────────────────
// Thin open/closed wrapper so the body (and its queries) only mount while the
// modal is actually open. The props and the behaviour seen by the page are
// unchanged.
function NewPRModal({ open, onClose, onCreated }: { open: boolean; onClose: () => void; onCreated: () => void }) {
  if (!open) return null;
  return <NewPRForm onClose={onClose} onCreated={onCreated} />;
}

function NewPRForm({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({
    date: localToday(), requiredByDate: '', departmentId: '', notes: '',
    // Header fields on PurchaseRequisitionModel. `department` is the canonical
    // name; `departmentId` above is the alias the shipped list view reads, so
    // the one dropdown writes both and neither read path changes.
    requestedBy: '', department: '', reason: '', reasonCategory: '', customerOrderRef: '',
    priority: 'normal', preferredSupplierId: '',
    lines: [{ ...EMPTY_PR_LINE }] as PRLine[],
  });
  const [saving, setSaving] = useState(false);

  // Reused pickers — one canonical source each, no new fetching layer:
  //   users    → shared/hooks/useUserOptions (GET /api/users)
  //   units    → features/catalog/hooks/useCatalogApi useUnits (GET /api/catalog/units)
  //   products → features/catalog/hooks/useCatalogApi useProducts (GET /api/catalog/products)
  const { data: users = [], isLoading: usersLoading } = useUserOptions();
  const { data: units = [], isLoading: unitsLoading } = useUnits();
  const { data: productPage, isLoading: productsLoading } = useProducts();
  const products = productPage?.data ?? [];
  // Gap-audit follow-up: Material Category / Reason were free text with no
  // way for an admin to add or rename an option — now real Master Data.
  const { data: materialCategories = [] } = useMasterItems('material_category');
  const { data: reasonCategories = [] } = useMasterItems('pr_reason');

  // Suppliers reuse the same AccountsVendor list the PO / Bill / Debit-Note
  // modals on this page already receive, so the preferred-supplier picker and
  // the actual buying screens resolve against one collection.
  const [vendors, setVendors] = useState<any[]>([]);
  useEffect(() => {
    api.get('/api/accounts/parties/vendors')
      .then(res => setVendors(Array.isArray(res.data) ? res.data : (res.data?.data || [])))
      .catch(() => {});
  }, []);

  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, { ...EMPTY_PR_LINE }] }));
  const setLine = (i: number, k: keyof PRLine, v: any) => setForm(f => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l) }));

  /** Selecting a product fills the blanks it can answer, never overwrites typing. */
  const selectProduct = (i: number, productId: string) => setForm(f => ({
    ...f,
    lines: f.lines.map((l, idx) => {
      if (idx !== i) return l;
      const p: any = products.find((x: any) => x._id === productId);
      if (!p) return { ...l, productId: '' };
      const unitId = idOf(p.unitId);
      const unit = units.find((u: any) => u._id === unitId);
      return {
        ...l,
        productId,
        description: l.description || p.name || '',
        quantityUnitId: l.quantityUnitId || unitId,
        quantityUnit: l.quantityUnit || (unit ? (unit.shortName || unit.name) : ''),
      };
    }),
  }));

  const selectUnit = (i: number, unitId: string) => {
    const unit = units.find((u: any) => u._id === unitId);
    setForm(f => ({
      ...f,
      lines: f.lines.map((l, idx) => idx === i ? {
        ...l, quantityUnitId: unitId, quantityUnit: unit ? (unit.shortName || unit.name) : '',
      } : l),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.lines.every(l => !l.description.trim() && !l.productId)) {
      alert('Add at least one line with a product or a description.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        requestedBy: optionalId(form.requestedBy),
        preferredSupplierId: optionalId(form.preferredSupplierId),
        lines: form.lines.map(l => ({
          description: l.description,
          quantity: l.quantity,
          estimatedUnitCostMinor: l.estimatedUnitCostMinor,
          productId: optionalId(l.productId),
          materialCategory: l.materialCategory,
          specification: l.specification,
          quantityUnitId: optionalId(l.quantityUnitId),
          quantityUnit: l.quantityUnit,
          expectedRateMinor: majorToMinor(l.expectedRate),
          targetRateMinor: majorToMinor(l.targetRate),
        })),
      };
      await api.post('/api/accounting/p2p/requisitions', payload);
      onCreated();
    } catch (err: any) { alert(err.response?.data?.error?.message || err.message); }
    finally { setSaving(false); }
  };

  const inputCls = 'w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-slate-100';

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-blue-600">
          <div className="flex items-center gap-2 text-white"><FileText className="w-5 h-5" /><h2 className="text-lg font-bold">New Purchase Requisition</h2></div>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-white/80" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Date *</label><input type="date" required className={inputCls} value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} /></div>
            <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Required By</label><input type="date" className={inputCls} value={form.requiredByDate} onChange={e => setForm(f => ({ ...f, requiredByDate: e.target.value }))} /></div>

            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Requested By</label>
              <select className={inputCls} value={form.requestedBy} onChange={e => setForm(f => ({ ...f, requestedBy: e.target.value }))} disabled={usersLoading}>
                <option value="">{usersLoading ? 'Loading users…' : 'Select requester…'}</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.display_name}{u.department ? ` — ${u.department}` : ''}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Department</label>
              <select
                className={inputCls}
                value={form.department}
                onChange={e => setForm(f => ({ ...f, department: e.target.value, departmentId: e.target.value }))}
              >
                <option value="">Select department…</option>
                {DEPARTMENT_OPTIONS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
              </select>
            </div>

            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Priority</label>
              <select className={inputCls} value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                {PR_PRIORITY_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Preferred Supplier</label>
              <select className={inputCls} value={form.preferredSupplierId} onChange={e => setForm(f => ({ ...f, preferredSupplierId: e.target.value }))}>
                <option value="">No preference</option>
                {vendors.map((v: any) => <option key={v._id} value={v._id}>{v.name} ({v.code || 'Vendor'})</option>)}
              </select>
            </div>

            <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Customer Order Ref</label><input className={inputCls} value={form.customerOrderRef} onChange={e => setForm(f => ({ ...f, customerOrderRef: e.target.value }))} placeholder="Sales order this is bought against" /></div>
            <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Notes</label><input className={inputCls} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>

            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Reason Category</label>
              <select className={inputCls} value={form.reasonCategory} onChange={e => setForm(f => ({ ...f, reasonCategory: e.target.value }))}>
                <option value="">Select a reason…</option>
                {reasonCategories.map((r: any) => <option key={r._id} value={r.name}>{r.name}</option>)}
              </select>
            </div>
            <div className="col-span-2"><label className="text-xs font-semibold text-slate-500 mb-1 block">Reason for Request</label><textarea rows={2} className={inputCls} value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} placeholder="Justification the approver will read" /></div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-slate-600">Requisition Lines</label>
              <button type="button" onClick={addLine} className="text-xs text-indigo-600 font-medium hover:underline">+ Add Line</button>
            </div>
            <div className="space-y-2">
              {form.lines.map((l, i) => (
                <div key={i} className="bg-slate-50 dark:bg-slate-800 rounded-lg p-3 space-y-2">
                  <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-2">
                      <label className="text-xs text-slate-400 mb-1 block">Product</label>
                      <select className={inputCls} value={l.productId} onChange={e => selectProduct(i, e.target.value)} disabled={productsLoading}>
                        <option value="">{productsLoading ? 'Loading products…' : 'Select a catalog product…'}</option>
                        {products.map((p: any) => <option key={p._id} value={p._id}>{p.name}{p.sku ? ` (${p.sku})` : ''}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Material Category</label>
                      <select className={inputCls} value={l.materialCategory} onChange={e => setLine(i, 'materialCategory', e.target.value)}>
                        <option value="">Select…</option>
                        {materialCategories.map((c: any) => <option key={c._id} value={c.name}>{c.name}</option>)}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-2">
                    <div className="col-span-2"><label className="text-xs text-slate-400 mb-1 block">Description</label><input className={inputCls} value={l.description} onChange={e => setLine(i, 'description', e.target.value)} placeholder="Item or service required" /></div>
                    <div><label className="text-xs text-slate-400 mb-1 block">Qty</label><input type="number" min="1" className={inputCls} value={l.quantity} onChange={e => setLine(i, 'quantity', Number(e.target.value))} /></div>
                  </div>

                  {/* Stock position for the selected product, read-only, next to Qty */}
                  <ProductStockPanel productId={l.productId} />

                  <div className="grid grid-cols-3 gap-2">
                    <div>
                      <label className="text-xs text-slate-400 mb-1 block">Unit</label>
                      <select className={inputCls} value={l.quantityUnitId} onChange={e => selectUnit(i, e.target.value)} disabled={unitsLoading}>
                        <option value="">{unitsLoading ? 'Loading units…' : 'Select unit…'}</option>
                        {units.map((u: any) => <option key={u._id} value={u._id}>{u.name}{u.shortName ? ` (${u.shortName})` : ''}</option>)}
                      </select>
                    </div>
                    <div><label className="text-xs text-slate-400 mb-1 block">Expected Rate (₹)</label><input type="number" min="0" step="0.01" className={inputCls} value={l.expectedRate} onChange={e => setLine(i, 'expectedRate', e.target.value)} placeholder="0.00" /></div>
                    <div><label className="text-xs text-slate-400 mb-1 block">Target Rate (₹)</label><input type="number" min="0" step="0.01" className={inputCls} value={l.targetRate} onChange={e => setLine(i, 'targetRate', e.target.value)} placeholder="0.00" /></div>
                  </div>

                  <div><label className="text-xs text-slate-400 mb-1 block">Specification</label><input className={inputCls} value={l.specification} onChange={e => setLine(i, 'specification', e.target.value)} placeholder="Grade, viscosity, packing or other spec" /></div>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm border hover:bg-slate-100 transition-colors">Cancel</button>
          <button type="submit" disabled={saving} className="px-5 py-2 rounded-lg text-sm font-semibold bg-indigo-600 text-white hover:bg-indigo-700 flex items-center gap-2 disabled:opacity-50 transition-colors">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}Create Requisition
          </button>
        </div>
      </form>
    </div>
  );
}

// ── New PO Modal ──────────────────────────────────────────────────────────────
function NewPOModal({ open, onClose, onCreated, vendors = [] }: { open: boolean; onClose: () => void; onCreated: () => void; vendors?: any[] }) {
  const [form, setForm] = useState({
    date: localToday(), expectedDeliveryDate: '', paymentTermsDays: 30,
    supplierName: '', supplierId: '', currency: 'INR', notes: '',
    freightMinor: 0, dutiesMinor: 0, insuranceMinor: 0, handlingMinor: 0,
    lines: [{ description: '', orderedQty: 1, unitCostMinor: 0, taxPct: 0, discountPct: 0 }],
  });
  const [saving, setSaving] = useState(false);

  const addLine = () => setForm(f => ({ ...f, lines: [...f.lines, { description: '', orderedQty: 1, unitCostMinor: 0, taxPct: 0, discountPct: 0 }] }));
  const setLine = (i: number, k: string, v: any) => setForm(f => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, [k]: v } : l) }));

  const lineTotal = form.lines.reduce((s, l) => s + Math.round(l.orderedQty * l.unitCostMinor * (1 - l.discountPct / 100)), 0);
  const taxTotal = form.lines.reduce((s, l) => s + Math.round(l.orderedQty * l.unitCostMinor * (1 - l.discountPct / 100) * l.taxPct / 100), 0);
  const extras = form.freightMinor + form.dutiesMinor + form.insuranceMinor + form.handlingMinor;
  const grandTotal = lineTotal + taxTotal + extras;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const lines = form.lines.map(l => ({
        ...l,
        unitCostMinor: Math.round(parseFloat(String(l.unitCostMinor)) * 100 || 0),
        description: l.description,
      }));
      await api.post('/api/accounting/p2p/pos', { ...form, lines, freightMinor: form.freightMinor * 100, dutiesMinor: form.dutiesMinor * 100 });
      onCreated();
    } catch (err: any) { alert(err.response?.data?.error?.message || err.message); }
    finally { setSaving(false); }
  };

  if (!open) return null;
  const inputCls = 'w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-slate-100';

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-emerald-600">
          <div className="flex items-center gap-2 text-white"><ShoppingCart className="w-5 h-5" /><h2 className="text-lg font-bold">New Purchase Order</h2></div>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-white/80" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Supplier / Vendor *</label>
              {vendors.length > 0 && (
                <select
                  className={`${inputCls} mb-1.5`}
                  onChange={e => {
                    const sel = vendors.find((v: any) => v._id === e.target.value);
                    if (sel) setForm(f => ({ ...f, supplierId: sel._id, supplierName: sel.name }));
                  }}
                >
                  <option value="">Select Existing Vendor...</option>
                  {vendors.map((v: any) => (
                    <option key={v._id} value={v._id}>{v.name} ({v.code || 'Vendor'})</option>
                  ))}
                </select>
              )}
              <input required className={inputCls} value={form.supplierName} onChange={e => setForm(f => ({ ...f, supplierName: e.target.value }))} placeholder="Supplier name or party ID" />
            </div>
            <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Currency</label><select className={inputCls} value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}>{['INR','USD','EUR','GBP','AED'].map(c => <option key={c}>{c}</option>)}</select></div>
            <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Date *</label><input type="date" required className={inputCls} value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} /></div>
            <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Expected Delivery</label><input type="date" className={inputCls} value={form.expectedDeliveryDate} onChange={e => setForm(f => ({ ...f, expectedDeliveryDate: e.target.value }))} /></div>
            <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Payment Terms (days)</label><input type="number" className={inputCls} value={form.paymentTermsDays} onChange={e => setForm(f => ({ ...f, paymentTermsDays: Number(e.target.value) }))} /></div>
          </div>

          {/* Lines */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-xs font-semibold text-slate-600">Order Lines</label>
              <button type="button" onClick={addLine} className="text-xs text-emerald-600 font-medium hover:underline">+ Add Line</button>
            </div>
            <div className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800"><tr>{['Description', 'Qty', 'Unit Cost (₹)', 'Disc %', 'Tax %', 'Line Total'].map(h => <th key={h} className="text-left px-3 py-2 text-xs font-semibold text-slate-500">{h}</th>)}</tr></thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {form.lines.map((l, i) => {
                    const lt = Math.round(l.orderedQty * (Number(l.unitCostMinor) || 0) * (1 - l.discountPct / 100));
                    const tax = Math.round(lt * l.taxPct / 100);
                    return (
                      <tr key={i} className="bg-white dark:bg-slate-900">
                        <td className="px-3 py-2"><input className="w-full text-sm bg-transparent border-b border-slate-200 focus:outline-none focus:border-indigo-400 py-1" value={l.description} onChange={e => setLine(i, 'description', e.target.value)} placeholder="Item / service" /></td>
                        <td className="px-3 py-2 w-20"><input type="number" min="0.001" step="any" className="w-full text-sm bg-transparent border-b border-slate-200 focus:outline-none text-center" value={l.orderedQty} onChange={e => setLine(i, 'orderedQty', Number(e.target.value))} /></td>
                        <td className="px-3 py-2 w-32"><input type="number" min="0" step="0.01" className="w-full text-sm bg-transparent border-b border-slate-200 focus:outline-none" value={Number(l.unitCostMinor) || ''} onChange={e => setLine(i, 'unitCostMinor', parseFloat(e.target.value || '0'))} placeholder="0.00" /></td>
                        <td className="px-3 py-2 w-16"><input type="number" min="0" max="100" className="w-full text-sm bg-transparent border-b border-slate-200 focus:outline-none text-center" value={l.discountPct} onChange={e => setLine(i, 'discountPct', Number(e.target.value))} /></td>
                        <td className="px-3 py-2 w-16"><input type="number" min="0" max="100" className="w-full text-sm bg-transparent border-b border-slate-200 focus:outline-none text-center" value={l.taxPct} onChange={e => setLine(i, 'taxPct', Number(e.target.value))} /></td>
                        <td className="px-3 py-2 w-28 text-right font-medium text-slate-700 dark:text-slate-300">{fmt((lt + tax))}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Additional charges */}
          <div className="grid grid-cols-4 gap-3">
            {[['Freight (₹)', 'freightMinor'], ['Duties (₹)', 'dutiesMinor'], ['Insurance (₹)', 'insuranceMinor'], ['Handling (₹)', 'handlingMinor']].map(([l, k]) => (
              <div key={k}><label className="text-xs text-slate-400 mb-1 block">{l}</label><input type="number" min="0" step="0.01" className={inputCls} value={Number(form[k as keyof typeof form]) || ''} onChange={e => setForm(f => ({ ...f, [k]: Number(e.target.value) }))} /></div>
            ))}
          </div>

          {/* Grand total */}
          <div className="flex justify-end">
            <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-xl px-6 py-3 text-right">
              <div className="text-xs text-slate-500">Grand Total</div>
              <div className="text-2xl font-black text-emerald-700">{fmt(grandTotal)}</div>
            </div>
          </div>
          <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Notes</label><textarea rows={2} className={inputCls} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm border hover:bg-slate-100 transition-colors">Cancel</button>
          <button type="submit" disabled={saving} className="px-5 py-2 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 flex items-center gap-2 disabled:opacity-50 transition-colors">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}Create PO
          </button>
        </div>
      </form>
    </div>
  );
}

// ── New GRN Modal ─────────────────────────────────────────────────────────────
// Receiving-time inspection outcome per line — the spec's "QC" pipeline
// step, captured where the goods are actually counted rather than as a
// separate stage. No fixed backend enum (GoodsReceiptNoteModel.lines
// .qualityStatus is free text, matching item-master.js's own receiving
// route for the same concept) — these three are just what this screen offers.
const QC_STATUS_OPTIONS = [
  { value: 'good', label: 'Good' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'rejected', label: 'Rejected' },
];

interface GrnLineDraft {
  poLineIndex: number;
  productId: string;
  description: string;
  orderedQty: number;
  receivedQty: number;
  unitCostMinor: number;
  acceptedQty: string;
  rejectedQty: string;
  qualityStatus: string;
  batchNumber: string;
}

function NewGRNModal({ open, onClose, onCreated, pos = [] }: { open: boolean; onClose: () => void; onCreated: () => void; pos?: any[] }) {
  const [form, setForm] = useState({ poId: '', date: localToday(), supplierDeliveryNote: '', notes: '' });
  const [lines, setLines] = useState<GrnLineDraft[]>([]);
  const [saving, setSaving] = useState(false);
  const [approving, setApproving] = useState(false);

  const selectedPo = pos.find((p: any) => p._id === form.poId || p.poNumber === form.poId);
  const isDraftPo = selectedPo && ['draft', 'submitted'].includes(selectedPo.status);

  // Selecting a PO seeds one draft line per PO line, pre-filled with the
  // full remaining (ordered - already received) quantity — the common case
  // is receiving everything still outstanding, adjustable down for a
  // partial or short delivery.
  useEffect(() => {
    if (!selectedPo) { setLines([]); return; }
    setLines((selectedPo.lines || []).map((l: any, idx: number) => {
      const pending = Math.max(0, (l.orderedQty || 0) - (l.receivedQty || 0));
      return {
        poLineIndex: idx,
        productId: typeof l.productId === 'object' ? l.productId?._id : l.productId,
        description: l.description || '',
        orderedQty: l.orderedQty || 0,
        receivedQty: l.receivedQty || 0,
        unitCostMinor: l.unitCostMinor || 0,
        acceptedQty: String(pending),
        rejectedQty: '0',
        qualityStatus: 'good',
        batchNumber: '',
      };
    }));
  }, [form.poId]);

  const setLine = (i: number, patch: Partial<GrnLineDraft>) =>
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));

  const handleQuickApprove = async () => {
    if (!selectedPo) return;
    setApproving(true);
    try {
      if (selectedPo.status === 'draft') {
        await api.post(`/api/accounting/p2p/pos/${selectedPo._id}/submit`);
      }
      await api.post(`/api/accounting/p2p/pos/${selectedPo._id}/approve`);
      selectedPo.status = 'approved';
    } catch (err: any) {
      alert(err.response?.data?.error?.message || err.message);
    } finally {
      setApproving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isDraftPo) {
      alert(`PO '${selectedPo.poNumber}' is currently in '${selectedPo.status}' status. Please click 'Approve PO' first before creating a Goods Receipt.`);
      return;
    }
    const receivedLines = lines.filter((l) => (Number(l.acceptedQty) || 0) > 0 || (Number(l.rejectedQty) || 0) > 0);
    if (receivedLines.length === 0) {
      alert('Enter an accepted or rejected quantity on at least one line.');
      return;
    }
    setSaving(true);
    try {
      await api.post('/api/accounting/p2p/grn', {
        ...form,
        lines: receivedLines.map((l) => ({
          poLineIndex: l.poLineIndex,
          productId: l.productId,
          description: l.description,
          orderedQty: l.orderedQty,
          acceptedQty: Number(l.acceptedQty) || 0,
          rejectedQty: Number(l.rejectedQty) || 0,
          unitCostMinor: l.unitCostMinor,
          qualityStatus: l.qualityStatus,
          batchNumber: l.batchNumber,
        })),
      });
      onCreated();
    } catch (err: any) { alert(err.response?.data?.error?.message || err.message); }
    finally { setSaving(false); }
  };

  if (!open) return null;
  const inputCls = 'w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-slate-100';
  const cellCls = 'w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-slate-100';

  const readyPos = pos.filter((p: any) => ['approved', 'sent', 'partially_received'].includes(p.status));
  const pendingPos = pos.filter((p: any) => ['draft', 'submitted'].includes(p.status));

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-amber-500">
          <div className="flex items-center gap-2 text-white">
            <Package className="w-5 h-5" /> <h2 className="text-lg font-bold">New Goods Receipt Note (GRN)</h2>
          </div>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-white/80 hover:text-white" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">PO Reference / Number *</label>
            {pos.length > 0 && (
              <select
                className={`${inputCls} mb-1.5`}
                onChange={e => setForm(f => ({ ...f, poId: e.target.value }))}
                value={form.poId}
              >
                <option value="">Select a Purchase Order...</option>
                {readyPos.length > 0 && (
                  <optgroup label="✅ Approved POs (Ready for Receipt)">
                    {readyPos.map((p: any) => (
                      <option key={p._id} value={p._id}>
                        {p.poNumber} — {p.supplierName || 'Vendor'} ({p.status})
                      </option>
                    ))}
                  </optgroup>
                )}
                {pendingPos.length > 0 && (
                  <optgroup label="⏳ Pending Approval POs">
                    {pendingPos.map((p: any) => (
                      <option key={p._id} value={p._id}>
                        {p.poNumber} — {p.supplierName || 'Vendor'} ({p.status} - Needs Approval)
                      </option>
                    ))}
                  </optgroup>
                )}
              </select>
            )}
            <input required className={inputCls} value={form.poId} onChange={e => setForm(f => ({ ...f, poId: e.target.value }))} placeholder="e.g. PO-00001 or Order ID" />
          </div>

          {isDraftPo && (
            <div className="p-3 bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/50 rounded-xl flex items-center justify-between gap-3 text-xs text-amber-800 dark:text-amber-300">
              <div>
                <span className="font-bold">Notice:</span> PO status is <span className="font-semibold">{selectedPo.status}</span>. Receipts require an approved PO.
              </div>
              <button
                type="button"
                onClick={handleQuickApprove}
                disabled={approving}
                className="px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-medium shadow-sm whitespace-nowrap flex items-center gap-1 disabled:opacity-50"
              >
                {approving && <Loader2 className="w-3 h-3 animate-spin" />} Approve PO Now
              </button>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Receipt Date *</label><input type="date" required className={inputCls} value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} /></div>
            <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Supplier Delivery Note / Challan #</label><input className={inputCls} value={form.supplierDeliveryNote} onChange={e => setForm(f => ({ ...f, supplierDeliveryNote: e.target.value }))} placeholder="Challan # or delivery note" /></div>
          </div>

          {/* Line-by-line receipt: accepted / rejected quantity plus the
              receiving-time QC outcome (spec's "QC" pipeline step) and batch
              tracking, per line. Previously this modal posted only header
              fields with no `lines` at all — every GRN created from here was
              an empty shell with zero accepted/rejected quantity. */}
          {selectedPo && (
            <div>
              <label className="text-xs font-semibold text-slate-500 mb-1 block">Line Items & QC — {selectedPo.poNumber}</label>
              <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-700">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                    <tr>
                      <th className="text-left px-2 py-2 font-semibold">Product / Description</th>
                      <th className="text-right px-2 py-2 font-semibold">Ordered</th>
                      <th className="text-right px-2 py-2 font-semibold">Received</th>
                      <th className="text-right px-2 py-2 font-semibold w-20">Accept</th>
                      <th className="text-right px-2 py-2 font-semibold w-20">Reject</th>
                      <th className="text-left px-2 py-2 font-semibold w-28">QC Status</th>
                      <th className="text-left px-2 py-2 font-semibold w-28">Batch #</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                    {lines.map((l, i) => {
                      const pending = Math.max(0, l.orderedQty - l.receivedQty);
                      return (
                        <tr key={i}>
                          <td className="px-2 py-1.5 text-slate-700 dark:text-slate-200">{l.description || '—'}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">{l.orderedQty}</td>
                          <td className="px-2 py-1.5 text-right tabular-nums text-slate-500">{l.receivedQty} <span className="text-slate-400">/ {pending} left</span></td>
                          <td className="px-2 py-1.5">
                            <input type="number" min="0" step="any" className={`${cellCls} text-right`} value={l.acceptedQty} onChange={e => setLine(i, { acceptedQty: e.target.value })} />
                          </td>
                          <td className="px-2 py-1.5">
                            <input type="number" min="0" step="any" className={`${cellCls} text-right`} value={l.rejectedQty} onChange={e => setLine(i, { rejectedQty: e.target.value })} />
                          </td>
                          <td className="px-2 py-1.5">
                            <select className={cellCls} value={l.qualityStatus} onChange={e => setLine(i, { qualityStatus: e.target.value })}>
                              {QC_STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                            </select>
                          </td>
                          <td className="px-2 py-1.5">
                            <input className={cellCls} value={l.batchNumber} onChange={e => setLine(i, { batchNumber: e.target.value })} placeholder="Optional" />
                          </td>
                        </tr>
                      );
                    })}
                    {lines.length === 0 && (
                      <tr><td colSpan={7} className="px-2 py-4 text-center text-slate-400">Select a Purchase Order above to load its lines.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Notes</label><textarea rows={2} className={inputCls} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
        </div>
        <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200 dark:border-slate-800">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm border hover:bg-slate-50">Cancel</button>
          <button type="submit" disabled={saving || isDraftPo} className="px-4 py-2 rounded-lg text-sm font-semibold bg-amber-600 text-white hover:bg-amber-700 flex items-center gap-2 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Create GRN
          </button>
        </div>
      </form>
    </div>
  );
}

// ── New Bill Modal ────────────────────────────────────────────────────────────
function NewBillModal({ open, onClose, onCreated, vendors = [] }: { open: boolean; onClose: () => void; onCreated: () => void; vendors?: any[] }) {
  const [form, setForm] = useState({ supplierId: '', supplierInvoiceNumber: '', date: localToday(), dueDate: '', notes: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/api/accounting/p2p/bills', form);
      onCreated();
    } catch (err: any) { alert(err.response?.data?.error?.message || err.message); }
    finally { setSaving(false); }
  };

  if (!open) return null;
  const inputCls = 'w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-slate-100';

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4">
        <div className="flex items-center justify-between border-b pb-3">
          <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <ReceiptText className="w-5 h-5 text-violet-600" /> New Supplier Bill
          </h2>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">Supplier Name / ID *</label>
            {vendors.length > 0 && (
              <select
                className={`${inputCls} mb-1.5`}
                onChange={e => {
                  const sel = vendors.find((v: any) => v._id === e.target.value);
                  if (sel) setForm(f => ({ ...f, supplierId: sel._id }));
                }}
              >
                <option value="">Select Existing Supplier...</option>
                {vendors.map((v: any) => (
                  <option key={v._id} value={v._id}>{v.name} ({v.code || 'Vendor'})</option>
                ))}
              </select>
            )}
            <input required className={inputCls} value={form.supplierId} onChange={e => setForm(f => ({ ...f, supplierId: e.target.value }))} placeholder="Supplier name or ID" />
          </div>
          <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Supplier Invoice # *</label><input required className={inputCls} value={form.supplierInvoiceNumber} onChange={e => setForm(f => ({ ...f, supplierInvoiceNumber: e.target.value }))} placeholder="e.g. INV-98765" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Bill Date *</label><input type="date" required className={inputCls} value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} /></div>
            <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Due Date</label><input type="date" className={inputCls} value={form.dueDate} onChange={e => setForm(f => ({ ...f, dueDate: e.target.value }))} /></div>
          </div>
          <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Notes</label><textarea rows={2} className={inputCls} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm border hover:bg-slate-50">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg text-sm font-semibold bg-violet-600 text-white hover:bg-violet-700 flex items-center gap-2 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Create Bill
          </button>
        </div>
      </form>
    </div>
  );
}

// ── New Debit Note Modal ──────────────────────────────────────────────────────
function NewDebitNoteModal({ open, onClose, onCreated, vendors = [] }: { open: boolean; onClose: () => void; onCreated: () => void; vendors?: any[] }) {
  const [form, setForm] = useState({ supplierId: '', reason: '', date: localToday(), notes: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post('/api/accounting/p2p/debit-notes', form);
      onCreated();
    } catch (err: any) { alert(err.response?.data?.error?.message || err.message); }
    finally { setSaving(false); }
  };

  if (!open) return null;
  const inputCls = 'w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:text-slate-100';

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <form onSubmit={handleSubmit} className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-lg p-6 space-y-4">
        <div className="flex items-center justify-between border-b pb-3">
          <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <RotateCcw className="w-5 h-5 text-rose-600" /> New Debit Note / Purchase Return
          </h2>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-slate-500 mb-1 block">Supplier Name / ID *</label>
            {vendors.length > 0 && (
              <select
                className={`${inputCls} mb-1.5`}
                onChange={e => {
                  const sel = vendors.find((v: any) => v._id === e.target.value);
                  if (sel) setForm(f => ({ ...f, supplierId: sel._id }));
                }}
              >
                <option value="">Select Existing Supplier...</option>
                {vendors.map((v: any) => (
                  <option key={v._id} value={v._id}>{v.name} ({v.code || 'Vendor'})</option>
                ))}
              </select>
            )}
            <input required className={inputCls} value={form.supplierId} onChange={e => setForm(f => ({ ...f, supplierId: e.target.value }))} placeholder="Supplier name or ID" />
          </div>
          <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Date *</label><input type="date" required className={inputCls} value={form.date} onChange={e => setForm(f => ({ ...f, date: e.target.value }))} /></div>
          <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Return Reason</label><input className={inputCls} value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} placeholder="e.g. Damaged goods, Price adjustment" /></div>
          <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Notes</label><textarea rows={2} className={inputCls} value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} /></div>
        </div>
        <div className="flex justify-end gap-2 pt-3 border-t">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm border hover:bg-slate-50">Cancel</button>
          <button type="submit" disabled={saving} className="px-4 py-2 rounded-lg text-sm font-semibold bg-rose-600 text-white hover:bg-rose-700 flex items-center gap-2 disabled:opacity-50">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />} Create Debit Note
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Generic list table ────────────────────────────────────────────────────────
function DocTable({ columns, rows, loading, emptyMsg }: {
  columns: string[]; rows: any[]; loading: boolean; emptyMsg: string;
}) {
  if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-indigo-500" /></div>;
  if (rows.length === 0) return <div className="text-center py-12 text-slate-400 text-sm">{emptyMsg}</div>;
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700">
          <tr>{columns.map(c => <th key={c} className="text-left px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{c}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
          {rows.map((row, i) => (
            <tr key={i} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">{row}</tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Main ProcureToPayPage ────────────────────────────────────────────────────
export function ProcureToPayPage() {
  const [activeTab, setActiveTab] = useState('requisitions');
  const [requisitions, setRequisitions] = useState<any[]>([]);
  const [pos, setPos] = useState<any[]>([]);
  const [grns, setGrns] = useState<any[]>([]);
  const [bills, setBills] = useState<any[]>([]);
  const [debitNotes, setDebitNotes] = useState<any[]>([]);
  const [vendors, setVendors] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [showNewPR, setShowNewPR] = useState(false);
  const [showNewPO, setShowNewPO] = useState(false);
  const [showNewGRN, setShowNewGRN] = useState(false);
  const [showNewBill, setShowNewBill] = useState(false);
  const [showNewDebitNote, setShowNewDebitNote] = useState(false);
  const [cashBankAccounts, setCashBankAccounts] = useState<any[]>([]);
  const [payingBill, setPayingBill] = useState<any | null>(null);
  const [payForm, setPayForm] = useState({ amount: 0, cashBankAccountCode: '', date: localToday(), reference: '' });
  const [paySaving, setPaySaving] = useState(false);

  useEffect(() => {
    api.get('/api/accounts/parties/vendors')
      .then(res => {
        const list = Array.isArray(res.data) ? res.data : (res.data?.data || []);
        setVendors(list);
      })
      .catch(() => {});
    api.get('/api/accounting/accounts')
      .then(res => {
        const list = Array.isArray(res.data?.data) ? res.data.data : [];
        setCashBankAccounts(list.filter((a: any) =>
          !a.isGroupHeader && (a.primaryGroup === 'Cash & Bank' || a.subtype === 'cash' || a.subtype === 'bank' ||
            (a.code && a.code.startsWith('10') && !['1000', '1100', '1200', '1300', '1310'].includes(a.code)))));
      })
      .catch(() => {});
  }, []);

  const openPay = (bill: any) => {
    setPayingBill(bill);
    setPayForm({ amount: (bill.outstandingMinor || 0) / 100, cashBankAccountCode: '', date: localToday(), reference: '' });
  };

  const submitPayment = async () => {
    if (!payingBill) return;
    if (!payForm.cashBankAccountCode) { alert('Select a cash/bank account.'); return; }
    if (!(payForm.amount > 0)) { alert('Amount must be greater than zero.'); return; }
    setPaySaving(true);
    try {
      await api.post(`/api/accounting/p2p/bills/${payingBill._id}/pay`, {
        amountMinor: Math.round(payForm.amount * 100),
        cashBankAccountCode: payForm.cashBankAccountCode,
        date: payForm.date,
        reference: payForm.reference,
      });
      setPayingBill(null);
      loadTab('bills');
    } catch (err: any) {
      alert(err.response?.data?.error?.message || err.message);
    } finally {
      setPaySaving(false);
    }
  };

  const loadTab = useCallback(async (tab: string) => {
    setLoading(true);
    try {
      switch (tab) {
        case 'requisitions': { const r = await api.get('/api/accounting/p2p/requisitions'); setRequisitions(r.data.data?.items || []); break; }
        case 'pos': { const r = await api.get('/api/accounting/p2p/pos'); setPos(r.data.data?.items || []); break; }
        case 'grn': { const r = await api.get('/api/accounting/p2p/grn'); setGrns(r.data.data?.items || []); break; }
        case 'bills': { const r = await api.get('/api/accounting/p2p/bills'); setBills(r.data.data?.items || []); break; }
        case 'debit-notes': { const r = await api.get('/api/accounting/p2p/debit-notes'); setDebitNotes(r.data.data?.items || []); break; }
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadTab(activeTab); }, [activeTab, loadTab]);

  const handleAction = async (endpoint: string, id: string, action: string, body?: any) => {
    try {
      await api.post(`/api/accounting/p2p/${endpoint}/${id}/${action}`, body || {});
      loadTab(activeTab);
    } catch (err: any) { alert(err.response?.data?.error?.message || err.message); }
  };

  const TABS = [
    { id: 'requisitions', label: 'Requisitions', icon: FileText, count: requisitions.length },
    { id: 'pos', label: 'Purchase Orders', icon: ShoppingCart, count: pos.length },
    { id: 'grn', label: 'Goods Receipts', icon: Package, count: grns.length },
    { id: 'bills', label: 'Supplier Bills', icon: ReceiptText, count: bills.length },
    { id: 'debit-notes', label: 'Debit Notes', icon: RotateCcw, count: debitNotes.length },
  ];

  const td = (content: React.ReactNode, cls = '') => <td className={`px-4 py-3 ${cls}`}>{content}</td>;

  const CREATE_ACTIONS = [
    { tab: 'requisitions', label: 'New Requisition', open: () => setShowNewPR(true) },
    { tab: 'pos', label: 'New PO', open: () => setShowNewPO(true) },
    { tab: 'grn', label: 'New GRN', open: () => setShowNewGRN(true) },
    { tab: 'bills', label: 'New Bill', open: () => setShowNewBill(true) },
    { tab: 'debit-notes', label: 'New Debit Note', open: () => setShowNewDebitNote(true) },
  ];

  return (
    <div>
      <AccountingSubNav />
      <div className="mx-auto max-w-[1580px] space-y-5 pb-16">

        {/* Header — every create action stays reachable; the one for the
            open tab is the primary action, the rest are neutral. */}
        <PageHeader
          icon={ShoppingCart}
          eyebrow="Purchases"
          title="Procure-to-Pay"
          description="Requisition → purchase order → goods receipt → supplier bill → payment, with 3-way match."
          actions={CREATE_ACTIONS.map((a) => (
            <Button
              key={a.tab}
              size="sm"
              variant={a.tab === activeTab ? 'primary' : 'outline'}
              onClick={a.open}
              className="whitespace-nowrap"
            >
              <Plus className="w-3.5 h-3.5" /> {a.label}
            </Button>
          ))}
        />

        {/* Lifecycle counts — click to open that stage */}
        <StatGrid cols={5}>
          <StatCard label="Requisitions" value={requisitions.length} icon={FileText} tone="primary" onClick={() => setActiveTab('requisitions')} active={activeTab === 'requisitions'} />
          <StatCard label="Purchase Orders" value={pos.length} icon={ShoppingCart} tone="info" onClick={() => setActiveTab('pos')} active={activeTab === 'pos'} />
          <StatCard label="Goods Receipts" value={grns.length} icon={Package} tone="warning" onClick={() => setActiveTab('grn')} active={activeTab === 'grn'} />
          <StatCard label="Supplier Bills" value={bills.length} icon={ReceiptText} tone="success" onClick={() => setActiveTab('bills')} active={activeTab === 'bills'} />
          <StatCard label="Debit Notes" value={debitNotes.length} icon={RotateCcw} tone="danger" onClick={() => setActiveTab('debit-notes')} active={activeTab === 'debit-notes'} />
        </StatGrid>

        {/* Tabs */}
        <div role="tablist" aria-label="Procure-to-pay stages" className="flex gap-1 border-b border-outline overflow-x-auto">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = activeTab === t.id;
            return (
              <button key={t.id} role="tab" aria-selected={active} onClick={() => setActiveTab(t.id)}
                className={`-mb-px flex items-center gap-2 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap ${active ? 'border-primary text-primary' : 'border-transparent text-on-surface-variant hover:text-on-surface'}`}>
                <Icon className="w-3.5 h-3.5" />{t.label}
                {t.count > 0 && <span className={`text-xs px-1.5 py-0.5 rounded-full ${active ? 'bg-primary/10 text-primary' : 'bg-surface-container-highest text-on-surface-variant'}`}>{t.count}</span>}
              </button>
            );
          })}
        </div>

        {/* Content */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm p-4">

          {/* REQUISITIONS */}
          {activeTab === 'requisitions' && (
            <DocTable
              columns={['PR Number', 'Date', 'Required By', 'Department', 'Priority', 'Status', 'Actions']}
              loading={loading}
              emptyMsg="No purchase requisitions yet. Click 'New Requisition' to create one."
              rows={requisitions.map((pr, i) => [
                td(<span className="font-mono text-xs font-semibold text-indigo-600">{pr.prNumber || `PR-${i+1}`}</span>),
                td(fmtDate(pr.date)),
                td(fmtDate(pr.requiredByDate)),
                td(pr.departmentId || '—'),
                td(
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full capitalize ${PR_PRIORITY_TONE[pr.priority] || PR_PRIORITY_TONE.normal}`}>
                    {pr.priority || 'normal'}
                  </span>
                ),
                td(<StatusBadge status={pr.status} />),
                td(
                  <div className="flex gap-1">
                    {pr.status === 'draft' && <button onClick={() => handleAction('requisitions', pr._id, 'submit')} className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200">Submit</button>}
                    {pr.status === 'submitted' && <button onClick={() => handleAction('requisitions', pr._id, 'approve')} className="text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200">Approve</button>}
                  </div>
                ),
              ])}
            />
          )}

          {/* POs */}
          {activeTab === 'pos' && (
            <DocTable
              columns={['PO Number', 'Date', 'Expected', 'Supplier', 'Grand Total', 'Status', 'Actions']}
              loading={loading}
              emptyMsg="No purchase orders yet. Click 'New PO' to create one."
              rows={pos.map((po, i) => [
                td(<span className="font-mono text-xs font-semibold text-emerald-700">{po.poNumber || `PO-${i+1}`}</span>),
                td(fmtDate(po.date)),
                td(fmtDate(po.expectedDeliveryDate)),
                td(po.supplierName || po.supplierId?.toString().slice(-8) || '—'),
                td(<span className="font-semibold">{po.grandTotalMinor ? fmt(po.grandTotalMinor) : '—'}</span>),
                td(<StatusBadge status={po.status} />),
                td(
                  <div className="flex gap-1 flex-wrap">
                    {po.status === 'draft' && <button onClick={() => handleAction('pos', po._id, 'submit')} className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200">Submit</button>}
                    {po.status === 'submitted' && <button onClick={() => handleAction('pos', po._id, 'approve')} className="text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200">Approve</button>}
                  </div>
                ),
              ])}
            />
          )}

          {/* GRNs */}
          {activeTab === 'grn' && (
            <DocTable
              columns={['GRN Number', 'Date', 'PO Ref', 'Accepted (₹)', 'Rejected (₹)', 'Status', 'Actions']}
              loading={loading}
              emptyMsg="No goods receipts yet."
              rows={grns.map((grn, i) => [
                td(<span className="font-mono text-xs font-semibold text-amber-700">{grn.grnNumber || `GRN-${i+1}`}</span>),
                td(fmtDate(grn.date)),
                td(<span className="text-xs text-slate-500">{grn.poId?.toString().slice(-8)}</span>),
                td(<span className="font-semibold text-emerald-600">{fmt(grn.totalAcceptedMinor || 0)}</span>),
                td(<span className="font-semibold text-red-500">{fmt(grn.totalRejectedMinor || 0)}</span>),
                td(<StatusBadge status={grn.status} />),
                td(grn.status === 'draft' && <button onClick={() => handleAction('grn', grn._id, 'post')} className="text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200">Post GRN</button>),
              ])}
            />
          )}

          {/* Bills */}
          {activeTab === 'bills' && (
            <DocTable
              columns={['Bill #', 'Invoice #', 'Date', 'Due Date', 'Total', 'Outstanding', 'Status', 'Actions']}
              loading={loading}
              emptyMsg="No supplier bills yet."
              rows={bills.map((b, i) => [
                td(<span className="font-mono text-xs font-semibold text-violet-700">{b.billNumber || `BILL-${i+1}`}</span>),
                td(<span className="text-xs text-slate-500">{b.supplierInvoiceNumber || '—'}</span>),
                td(fmtDate(b.date)),
                td(fmtDate(b.dueDate)),
                td(<span className="font-semibold">{fmt(b.grandTotalMinor || 0)}</span>),
                td(<span className={`font-semibold ${b.outstandingMinor > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>{fmt(b.outstandingMinor || 0)}</span>),
                td(<StatusBadge status={b.status} />),
                td(
                  b.status === 'draft'
                    ? <button onClick={() => handleAction('bills', b._id, 'post')} className="text-xs px-2 py-1 rounded bg-emerald-100 text-emerald-700 hover:bg-emerald-200">Post Bill</button>
                    : ['posted', 'partially_paid'].includes(b.status) && (b.outstandingMinor || 0) > 0
                      ? <button onClick={() => openPay(b)} className="text-xs px-2 py-1 rounded bg-violet-100 text-violet-700 hover:bg-violet-200">Pay</button>
                      : null,
                ),
              ])}
            />
          )}

          {/* Debit Notes */}
          {activeTab === 'debit-notes' && (
            <DocTable
              columns={['Debit Note #', 'Date', 'Total Return', 'Reason', 'Status', 'Actions']}
              loading={loading}
              emptyMsg="No debit notes / purchase returns yet."
              rows={debitNotes.map((dn, i) => [
                td(<span className="font-mono text-xs font-semibold text-rose-700">{dn.debitNoteNumber || `DN-${i+1}`}</span>),
                td(fmtDate(dn.date)),
                td(<span className="font-semibold text-rose-600">{fmt(dn.grandTotalMinor || 0)}</span>),
                td(dn.reason || '—'),
                td(<StatusBadge status={dn.status} />),
                td(dn.status === 'draft' && (
                  <div className="flex gap-1">
                    <button onClick={() => handleAction('debit-notes', dn._id, 'post', { returnedAfterBilling: false })} className="text-xs px-2 py-1 rounded bg-amber-100 text-amber-700 hover:bg-amber-200">Post (Pre-Bill)</button>
                    <button onClick={() => handleAction('debit-notes', dn._id, 'post', { returnedAfterBilling: true })} className="text-xs px-2 py-1 rounded bg-rose-100 text-rose-700 hover:bg-rose-200">Post (Post-Bill)</button>
                  </div>
                )),
              ])}
            />
          )}
        </div>
      </div>

      <NewPRModal open={showNewPR} onClose={() => setShowNewPR(false)} onCreated={() => { setShowNewPR(false); loadTab('requisitions'); if (activeTab !== 'requisitions') setActiveTab('requisitions'); }} />
      <NewPOModal open={showNewPO} onClose={() => setShowNewPO(false)} vendors={vendors} onCreated={() => { setShowNewPO(false); loadTab('pos'); if (activeTab !== 'pos') setActiveTab('pos'); }} />
      <NewGRNModal open={showNewGRN} onClose={() => setShowNewGRN(false)} pos={pos} onCreated={() => { setShowNewGRN(false); loadTab('grn'); if (activeTab !== 'grn') setActiveTab('grn'); }} />
      <NewBillModal open={showNewBill} onClose={() => setShowNewBill(false)} vendors={vendors} onCreated={() => { setShowNewBill(false); loadTab('bills'); if (activeTab !== 'bills') setActiveTab('bills'); }} />
      <NewDebitNoteModal open={showNewDebitNote} onClose={() => setShowNewDebitNote(false)} vendors={vendors} onCreated={() => { setShowNewDebitNote(false); loadTab('debit-notes'); if (activeTab !== 'debit-notes') setActiveTab('debit-notes'); }} />

      {payingBill && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between border-b pb-3">
              <h2 className="text-lg font-bold text-slate-900 dark:text-slate-100">Pay Bill {payingBill.billNumber}</h2>
              <button type="button" onClick={() => setPayingBill(null)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <p className="text-xs text-slate-500">Outstanding: <span className="font-semibold text-amber-600">{fmt(payingBill.outstandingMinor || 0)}</span></p>
            <div className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Amount (₹) *</label>
                <input type="number" min={0.01} step="0.01" className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 dark:text-slate-100" value={payForm.amount} onChange={e => setPayForm(f => ({ ...f, amount: Number(e.target.value) }))} />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500 mb-1 block">Pay From (Cash/Bank) *</label>
                <select className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 dark:text-slate-100" value={payForm.cashBankAccountCode} onChange={e => setPayForm(f => ({ ...f, cashBankAccountCode: e.target.value }))}>
                  <option value="">Select account…</option>
                  {cashBankAccounts.map((a: any) => <option key={a._id} value={a.code}>{a.code} — {a.name}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Date</label><input type="date" className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-slate-100" value={payForm.date} onChange={e => setPayForm(f => ({ ...f, date: e.target.value }))} /></div>
                <div><label className="text-xs font-semibold text-slate-500 mb-1 block">Reference</label><input className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm dark:text-slate-100" placeholder="UTR / cheque no." value={payForm.reference} onChange={e => setPayForm(f => ({ ...f, reference: e.target.value }))} /></div>
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t">
              <button type="button" onClick={() => setPayingBill(null)} className="px-4 py-2 rounded-lg text-sm border hover:bg-slate-50">Cancel</button>
              <button type="button" onClick={submitPayment} disabled={paySaving} className="px-4 py-2 rounded-lg text-sm font-semibold bg-violet-600 text-white hover:bg-violet-700 flex items-center gap-2 disabled:opacity-50">
                {paySaving && <Loader2 className="w-4 h-4 animate-spin" />} Record Payment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProcureToPayPage;
